import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { AxiosError } from "axios";
import {
  EditOfferProductAvailability,
  OfferSuccess,
} from "../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { NewOfferData } from "./Types";
import { emptyOffer, getProducts } from "services/storeFront";
import { useAppSelector } from "store";
import { getQueryStringParams } from "utils/queryString";

type NewOfferProviderProps = {
  prospectId: string;
} & PropsWithChildren;

const NewOfferProvider = ({ prospectId, children }: NewOfferProviderProps) => {
  const location = useLocation();
  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const identity = useAppSelector((state) => state.auth.identity);
  const [offerId, setOfferId] = useState<string>();
  const [lockVersion, setLockVersion] = useState<number>();
  const [storedOffer, setStoredOffer] = useState<OfferSuccess>();
  const [productAvailability, setProductAvailability] =
    useState<EditOfferProductAvailability>();
  const [error, setError] = useState<AxiosError>();
  const [isLoading, setIsLoading] = useState<boolean>();

  const fetchAndSetProductAvailability = useCallback(
    async (offer: OfferSuccess) => {
      if (!cognitoToken || !offer.id || !identity) {
        return;
      }
      const ePermitting =
        getQueryStringParams()?.ePermitting?.toString() === "true";
      const products = await getProducts(
        cognitoToken,
        offer.id,
        offer.opportunity.state,
        offer.opportunity.acpMarketType,
        ePermitting,
        identity
      );
      setProductAvailability(products);
    },
    [cognitoToken, identity]
  );

  const refreshProductAvailability = useCallback(async () => {
    if (!storedOffer) return;
    setIsLoading(true);
    await fetchAndSetProductAvailability(storedOffer);
    setIsLoading(false);
  }, [storedOffer, fetchAndSetProductAvailability]);

  const fetchNewOffer = useCallback(
    async (updatedFields?: string[]) => {
      if (!cognitoToken || !identity || !prospectId) {
        return;
      }
      if (updatedFields && updatedFields.length === 0) {
        // If this is an update, but no fields change - there is no need to update
        return;
      }
      try {
        setIsLoading(true);
        const offer = await emptyOffer(cognitoToken, prospectId, updatedFields);
        setStoredOffer(offer);
        if (offer.id !== offerId) {
          await fetchAndSetProductAvailability(offer);
          setOfferId(offer.id);
          setLockVersion(offer.lockVersion);
          setError(undefined);
        }
        setIsLoading(false);
      } catch (e: any) {
        setError(e);
        setIsLoading(false);
      }
    },
    [
      cognitoToken,
      identity,
      offerId,
      prospectId,
      fetchAndSetProductAvailability,
    ]
  );

  useEffect(() => {
    // Try to fetch the initial offer whenever the token or prospect change - as long as we haven't started loading yet
    if (isLoading === undefined) {
      const unsignedSourceProposalId = new URLSearchParams(location.search).get(
        "unsignedSourceProposalId"
      );
      const skipProviderInitialEmptyOffer =
        (location.pathname.includes("/offer/change-order") &&
          Boolean(unsignedSourceProposalId)) ||
        location.pathname.includes("/offer/copy/");
      if (skipProviderInitialEmptyOffer) {
        // CreatePage / CopyOfferPage calls emptyOffer; avoid duplicate /empty
        setIsLoading(false);
        return;
      }
      fetchNewOffer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cognitoToken,
    isLoading,
    prospectId,
    identity,
    location.pathname,
    location.search,
  ]);

  const completeValue =
    offerId && lockVersion !== undefined && productAvailability
      ? {
          offerId,
          lockVersion,
          productAvailability,
        }
      : undefined;
  const isLoadingComplete = !isLoading && (error || completeValue);
  const value = isLoadingComplete ? (error ? error : completeValue) : undefined;

  return (
    <FetchProductAvailabilityContext.Provider
      value={refreshProductAvailability}
    >
      <NewOfferContext.Provider value={value}>
        <SalesforceUpdatedContext.Provider value={fetchNewOffer}>
          <OfferConsumedContext.Provider value={fetchNewOffer}>
            {children}
          </OfferConsumedContext.Provider>
        </SalesforceUpdatedContext.Provider>
      </NewOfferContext.Provider>
    </FetchProductAvailabilityContext.Provider>
  );
};

const NewOfferContext = createContext<NewOfferData | AxiosError | undefined>(
  undefined
);
const FetchProductAvailabilityContext = createContext(() => {});
const SalesforceUpdatedContext = createContext(
  async (updatedFields?: string[]) => {}
);
const OfferConsumedContext = createContext(() => {});

export {
  FetchProductAvailabilityContext,
  NewOfferContext,
  SalesforceUpdatedContext,
  NewOfferProvider,
  OfferConsumedContext,
};
